import { useEffect, useState } from "react";
import { useAuthedApi } from "@/api/useAuthedApi";
import type { OrgMembership, OrgsResponse } from "@/api/companiesApi";

/** Module-level cache: org memberships are stable for a session (switching
 *  orgs reloads the page); every consumer (sidebar footer, account menu)
 *  shares one fetch. Mirrors useCompany. */
let cached: OrgsResponse | null = null;
let inflight: Promise<OrgsResponse | null> | null = null;

/** Test hook — clears the module cache between cases. */
export function _resetOrgsCache(): void {
  cached = null;
  inflight = null;
}

export interface UseOrgsResult {
  orgs: OrgMembership[];
  /** The org the session is actually scoped to (server-flagged), NOT merely
   *  the first membership — the two differ after an org switch. */
  activeOrg: OrgMembership | null;
}

export function useOrgs(): UseOrgsResult {
  const { companies } = useAuthedApi();
  const [data, setData] = useState<OrgsResponse | null>(cached);

  useEffect(() => {
    if (cached || !companies) return;
    inflight ??= companies.orgs().then(
      (o) => { cached = o; return o; },
      () => {
        // Transient error → consumers fall back to useCompany/generic copy.
        // Clear inflight so the NEXT mount retries.
        inflight = null;
        return null;
      },
    );
    let cancelled = false;
    inflight.then((o) => { if (!cancelled && o) setData(o); });
    return () => { cancelled = true; };
  }, [companies]);

  const orgs = data?.orgs ?? [];
  return { orgs, activeOrg: orgs.find((o) => o.active) ?? null };
}
